import { Link } from 'react-router-dom';
import { Check, X, EyeOff, Globe, LayoutGrid, Users, Code2, Camera, ArrowRight } from 'lucide-react';
import SEOHead from '../SEOHead';
import RelatedLinks from './RelatedLinks';

const ICONS = { invisible: EyeOff, site: Globe, dashboard: LayoutGrid, pricing: Users, script: Code2, capture: Camera };

const H2 = ({ children }) => <h2 className="text-xl font-display font-bold text-ink tracking-tight mb-5">{children}</h2>;

/* ── Verdict tiles: four numbers that settle it ──────────────────── */
const Verdict = ({ competitor, items }) => (
  <section aria-label="At a glance">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {items.map(({ label, cayda, other }) => (
        <div key={label} className="bg-white border border-ink-faint/20 rounded-2xl p-4 flex flex-col">
          <p className="text-2xs font-mono uppercase tracking-widest text-ink-muted mb-3">{label}</p>
          <p className="text-xl font-display font-bold text-ink leading-tight">{cayda}</p>
          <p className="text-2xs font-display font-medium text-accent mt-1">Cayda</p>
          <div className="mt-3 pt-3 border-t border-ink-faint/15">
            <p className="text-sm font-display text-ink-muted leading-snug">{other}</p>
            <p className="text-2xs font-display text-ink-faint mt-0.5">{competitor}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

/* ── Why Cayda: icon-led advantage cards ──────────────────────────── */
const Advantages = ({ competitor, items }) => (
  <section>
    <H2>Why teams choose Cayda over {competitor}</H2>
    <div className="grid sm:grid-cols-2 gap-3">
      {items.map(({ icon, title, body }) => {
        const Icon = ICONS[icon] || Check;
        return (
          <div key={title} className="bg-white border border-ink-faint/20 rounded-2xl p-5">
            <div className="w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center mb-3">
              <Icon className="w-4 h-4 text-accent" />
            </div>
            <p className="text-[15px] font-display font-semibold text-ink mb-1.5">{title}</p>
            <p className="text-sm font-display text-ink-secondary leading-relaxed">{body}</p>
          </div>
        );
      })}
    </div>
  </section>
);

/* ── Team cost: paired bars per scenario ──────────────────────────── */
const money = (n) => (typeof n === 'number' ? `$${n}` : n);

const Bar = ({ name, value, plan, cayda, width }) => (
  <div className="flex items-center gap-3">
    <span className={`w-20 shrink-0 text-xs font-display truncate ${cayda ? 'font-medium text-accent' : 'text-ink-muted'}`}>{name}</span>
    <div className="flex-1 h-6 bg-surface-sunken rounded-md overflow-hidden">
      <div className={`h-full rounded-md ${cayda ? 'bg-accent' : 'bg-ink-faint/60'}`} style={{ width }} />
    </div>
    <span className={`w-16 shrink-0 text-sm font-display font-semibold tabular-nums ${cayda ? 'text-ink' : 'text-ink-secondary'}`}>
      {money(value)}{typeof value === 'number' && <span className="text-xs font-normal text-ink-muted">/mo</span>}
    </span>
    <span className="w-36 shrink-0 text-2xs font-display text-ink-muted truncate hidden sm:block">{plan}</span>
  </div>
);

const TeamCost = ({ competitor, note, scenarios }) => {
  const max = Math.max(...scenarios.flatMap(s => [s.cayda, s.other].filter(v => typeof v === 'number')), 1);
  const w = (v) => (typeof v === 'number' ? `${Math.max(3, Math.round((v / max) * 100))}%` : '100%');
  return (
    <section>
      <H2>What a team actually pays</H2>
      {note && <p className="text-sm text-ink-muted mb-5 -mt-2">{note}</p>}
      <div className="bg-white border border-ink-faint/20 rounded-2xl p-5 sm:p-6 space-y-6">
        {scenarios.map(({ label, detail, cayda, other, caydaPlan, otherPlan }) => (
          <div key={label}>
            <div className="flex items-baseline justify-between gap-4 mb-2.5">
              <p className="text-[15px] font-display font-semibold text-ink">{label}</p>
              {detail && <p className="text-xs font-display text-ink-muted text-right">{detail}</p>}
            </div>
            <div className="space-y-1.5">
              <Bar name="Cayda" value={cayda} plan={caydaPlan} width={w(cayda)} cayda />
              <Bar name={competitor} value={other} plan={otherPlan} width={w(other)} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ── Feature grid: checks, crosses, short text; Cayda column lifted ── */
const Cell = ({ v, cayda }) => {
  if (v === true) return <Check className={`w-4 h-4 mx-auto ${cayda ? 'text-accent' : 'text-ink-secondary'}`} aria-label="Yes" />;
  if (v === false) return <X className="w-4 h-4 mx-auto text-ink-muted" aria-label="No" />;
  return <span className={cayda ? 'text-ink font-medium' : 'text-ink-muted'}>{v}</span>;
};

const FeatureGrid = ({ competitor, note, rows }) => (
  <section>
    <H2>Feature by feature</H2>
    {note && <p className="text-sm text-ink-muted mb-5 -mt-2">{note}</p>}
    <div className="overflow-x-auto -mx-6 px-6">
      <table className="w-full text-sm font-display border-separate border-spacing-0">
        <thead>
          <tr>
            <th className="text-left py-3 pr-4 font-semibold text-ink border-b border-ink-faint/30"> </th>
            <th className="text-center py-3 px-4 font-semibold text-accent bg-accent/5 rounded-t-xl border-b border-accent/20">Cayda</th>
            <th className="text-center py-3 pl-4 font-semibold text-ink-muted border-b border-ink-faint/30">{competitor}</th>
          </tr>
        </thead>
        <tbody className="text-ink-secondary">
          {rows.map(([label, cayda, other], i) => (
            <tr key={label}>
              <td className={`py-2.5 pr-4 text-ink ${i < rows.length - 1 ? 'border-b border-ink-faint/10' : ''}`}>{label}</td>
              <td className={`py-2.5 px-4 text-center bg-accent/5 ${i < rows.length - 1 ? 'border-b border-accent/10' : 'rounded-b-xl'}`}><Cell v={cayda} cayda /></td>
              <td className={`py-2.5 pl-4 text-center ${i < rows.length - 1 ? 'border-b border-ink-faint/10' : ''}`}><Cell v={other} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

/* ── Where the review happens: your domain vs. a proxy / visible widget ── */
const Browser = ({ url, urlTone = 'accent', children }) => (
  <div className="rounded-xl border border-ink-faint/30 bg-white overflow-hidden">
    <div className="flex items-center gap-2 px-3 py-2 border-b border-ink-faint/20 bg-surface-raised">
      <span className="flex gap-1"><i className="w-2 h-2 rounded-full bg-ink-faint block" /><i className="w-2 h-2 rounded-full bg-ink-faint block" /><i className="w-2 h-2 rounded-full bg-ink-faint block" /></span>
      <span className={`ml-1 text-2xs font-mono px-2 py-0.5 rounded bg-white border border-ink-faint/30 ${urlTone === 'accent' ? 'text-ink' : 'text-ink-muted'}`}>{url}</span>
    </div>
    <div className="p-4 min-h-[112px] relative">{children}</div>
  </div>
);

const Skeleton = () => (
  <div className="space-y-2 opacity-60">
    <div className="h-2.5 w-2/3 bg-ink-faint/40 rounded" />
    <div className="h-2 w-full bg-ink-faint/30 rounded" />
    <div className="h-2 w-5/6 bg-ink-faint/30 rounded" />
    <div className="h-2 w-1/2 bg-ink-faint/30 rounded" />
  </div>
);

const Pin = () => (
  <span className="absolute top-4 right-5 w-6 h-6 rounded-full bg-accent text-white text-2xs font-display font-bold flex items-center justify-center shadow-md ring-4 ring-accent/15">1</span>
);

const SiteLoadDiagram = ({ competitor, mode }) => (
  <section>
    <H2>Where the review actually happens</H2>
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-accent/5 border border-accent/20 rounded-2xl p-4">
        <p className="text-2xs font-mono uppercase tracking-widest text-accent mb-3">Cayda · your live site</p>
        <Browser url="https://your-site.com/pricing">
          <Skeleton />
          <Pin />
        </Browser>
        <div className="mt-3 space-y-1.5">
          <p className="text-xs font-display text-ink flex items-center gap-2"><Check className="w-3.5 h-3.5 text-accent shrink-0" />Team members see the feedback layer on the real page</p>
          <p className="text-xs font-display text-ink flex items-center gap-2"><EyeOff className="w-3.5 h-3.5 text-accent shrink-0" />Visitors see nothing, on any plan</p>
          <p className="text-xs font-display text-ink flex items-center gap-2"><Code2 className="w-3.5 h-3.5 text-accent shrink-0" />One script tag; works behind login and in single page apps</p>
        </div>
      </div>
      <div className="bg-white border border-ink-faint/20 rounded-2xl p-4">
        <p className="text-2xs font-mono uppercase tracking-widest text-ink-muted mb-3">{competitor} · {mode === 'proxy' ? 'a copy of your site' : 'a widget on your site'}</p>
        {mode === 'proxy' ? (
          <Browser url={`https://app.${competitor.toLowerCase().replace(/[^a-z]/g, '')}.com/review/…`} urlTone="muted">
            <div className="absolute inset-3 rounded-lg border border-dashed border-ink-faint/60 p-3">
              <Skeleton />
              <span className="absolute -top-2 left-3 px-1.5 text-2xs font-mono bg-white text-ink-muted">your site, fetched through their servers</span>
            </div>
          </Browser>
        ) : (
          <Browser url="https://your-site.com/pricing" urlTone="muted">
            <Skeleton />
            <span className="absolute bottom-3 right-3 px-2.5 py-1 rounded-md bg-ink text-white text-2xs font-display font-medium shadow">Feedback</span>
          </Browser>
        )}
        <div className="mt-3 space-y-1.5">
          {mode === 'proxy' ? (
            <>
              <p className="text-xs font-display text-ink-secondary flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5 text-ink-muted shrink-0" />Reviewers open a separate link, not your domain</p>
              <p className="text-xs font-display text-ink-secondary flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5 text-ink-muted shrink-0" />Pages behind login and dynamic apps need workarounds</p>
              <p className="text-xs font-display text-ink-secondary flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5 text-ink-muted shrink-0" />The copy can drift from what you actually shipped</p>
            </>
          ) : (
            <>
              <p className="text-xs font-display text-ink-secondary flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5 text-ink-muted shrink-0" />A launcher renders on the page for every visitor by default</p>
              <p className="text-xs font-display text-ink-secondary flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5 text-ink-muted shrink-0" />Hiding it on production is a setting you configure and maintain</p>
              <p className="text-xs font-display text-ink-secondary flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5 text-ink-muted shrink-0" />Built around collecting reports from the public, not your team</p>
            </>
          )}
        </div>
      </div>
    </div>
  </section>
);

/* ── Prose ─────────────────────────────────────────────────────────── */
const Prose = ({ title, paragraphs }) => (
  <section>
    <H2>{title}</H2>
    <div className="space-y-3 text-[15px] font-display text-ink-secondary leading-relaxed">
      {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
    </div>
  </section>
);

/**
 * Renders a /vs/<competitor> page from a data object. See VsMarkerIo.jsx for the shape.
 */
export default function ComparePage({ slug, competitor, description, hero, intro, verdict, advantages, cost, features, diagram, approach, bottomLine }) {
  return (
    <>
      <SEOHead
        title={`${competitor} Alternative — Cayda vs ${competitor}`}
        description={description}
        path={`/vs/${slug}`}
      />
      <div className="min-h-screen bg-cream">
        <header className="border-b border-ink-faint/20 bg-cream/80 backdrop-blur-xl">
          <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
            <Link to="/" className="text-base font-display font-semibold tracking-tight text-ink">Cayda</Link>
            <Link to="/sign-up" className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free</Link>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-6 pt-16 pb-10">
          <p className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-4">Cayda vs {competitor}</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-ink tracking-tight leading-tight mb-5">{hero}</h1>
          <p className="text-base font-display text-ink-secondary leading-relaxed max-w-xl">{intro}</p>
        </div>

        <main className="max-w-3xl mx-auto px-6 pb-20">
          <div className="space-y-14">
            <Verdict competitor={competitor} items={verdict} />
            <Advantages competitor={competitor} items={advantages} />
            <TeamCost competitor={competitor} note={cost.note} scenarios={cost.scenarios} />
            {diagram && <SiteLoadDiagram competitor={competitor} mode={diagram} />}
            <FeatureGrid competitor={competitor} note={features.note} rows={features.rows} />
            <Prose title={`How ${competitor} approaches it`} paragraphs={approach} />
            <Prose title="The bottom line" paragraphs={bottomLine} />
            <RelatedLinks current={`/vs/${slug}`} />
          </div>

          <div className="mt-16 pt-12 border-t border-ink-faint/20 text-center">
            <h3 className="text-xl font-display font-bold text-ink tracking-tight mb-2">Try it on your site.</h3>
            <p className="text-sm font-display text-ink-muted mb-5">Free for 2 sites and 3 team members. No trial clock, no credit card.</p>
            <Link to="/sign-up" className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free →</Link>
          </div>
        </main>

        <footer className="border-t border-ink-faint/20 bg-cream">
          <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
            <p className="text-xs font-display text-ink-faint">© {new Date().getFullYear()} Slowfast LLC</p>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Privacy</Link>
              <Link to="/terms" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Terms</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
